/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import React, {useEffect, useState} from 'react';
import {FormCheck, InputGroup} from "react-bootstrap";
import {DateTime} from 'luxon';
import PropTypes from 'prop-types';
import {findIndex, assign, truncate} from 'lodash';

import {useDispatch, useSelector} from "react-redux";
import {getManagedMatchTemplates, getManagedMatchTemplatesCount, setCurrentPage, setOrder, setSelectedId} from "./managedMatchTemplatesSlice";

import TextHoverDisplay from "../../common/components/TextHoverDisplay";
import TableWithPaging from '../../common/components/TableWithPaging';

import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {APP_COLORS} from "../../common/Constants";
import {faCheck} from "@fortawesome/pro-solid-svg-icons";
import CheckBox from "../../common/components/CheckBox";

const ManagedMatchTemplatesTable = (props) => {
    const dispatch = useDispatch();
    const managedMatchTemplatesState = useSelector((state) => state.managedMatchTemplates);

    useEffect(() => {
        // When open dialog, we clear state.
        dispatch(getManagedMatchTemplatesCount());
        dispatch(getManagedMatchTemplates());
    }, [props.lastReload, dispatch]);

    const createHeader = (row) => {
        return [
            {
                id: 1,
                name: '',
                property: 'id',
                sortable: false,
                formatted: true,
                formatBy: (row) => {
                    // let image = tbdBrainLightningIcon;
                    return <div>
                        <CheckBox
                            innerState
                            onChange={value =>
                                dispatch(setSelectedId({
                                    id: row.id,
                                    name: row.name,
                                    type: row.type,
                                    status: row.status,
                                    isChecked: value
                                }))
                            }
                        />
                    </div>
                }
            },
            {
                id: 2,
                name: 'Id',
                property: 'id',
                sortable: false,
                formatted: true,
                formatBy: (row) => {
                    if (row.id) {
                        return <TextHoverDisplay
                            truncatedText={row.id.substring(0, 13)}
                            fullText={row.id}
                        />
                    }
                    return <span></span>
                }
            },
            {
                id: 3,
                name: 'Match Type Id',
                property: 'matchTypeId',
                sortable: true,
                formatted: true,
                formatBy: (row) => {
                    if (row.matchTypeId) {
                        return <TextHoverDisplay
                            truncatedText={row.matchTypeId.substring(0, 13)}
                            fullText={row.matchTypeId}
                        />
                    }
                    return <span></span>
                }
            },
            {
                id: 4,
                name: 'Default',
                property: '',
                sortable: false,
                formatted: true,
                formatBy: (row) => {
                    if (row.default) {
                        return <span><FontAwesomeIcon color={APP_COLORS.green} icon={faCheck} /></span>
                    }
                    return <span></span>
                }
            },
            {
                id: 6,
                name: 'Name',
                property: 'name',
                sortable: true,
                formatted: false
            },
            {
                id: 7,
                name: 'Description',
                property: 'description',
                sortable: false,
                formatted: true,
                formatBy: (row) => {
                    if (row.description) {
                       return <span> {`${truncate(row.description, {length: 100, omission: '...'})}`} </span>
                    }
                    return <span></span>
                }
            },
            {
                id: 8,
                name: 'Config',
                property: 'config',
                sortable: false,
                formatted: true,
                formatBy: (row) => {
                    if (row.config) {
                        return <span> {`${truncate(JSON.stringify(row.config), {length: 100, omission: '...'})}`} </span>
                    }
                    return <span></span>
                }
            },
            {
                id: 9,
                name: 'Created',
                property: 'created',
                sortable: true,
                formatted: true,
                formatBy: (row, header) => {
                    return DateTime.fromISO(row.created).toLocaleString(DateTime.DATETIME_MED);
                }
            },
        ];
    };

    const headersUtil = (hds, hd, sortOrder) => {
        const _headers = hds.map(hc => assign(hc, {sortOrder: null}));
        _headers.splice(
            findIndex(hds, {id: hd.id}),
            1,
            assign({}, hd, {
                sortOrder: sortOrder,
            })
        );
        return _headers;
    };

    const handleSort = (header) => {
        if (!header.sortable) return;
        let _sortOrder;
        if (!header.sortOrder) _sortOrder = 'ASC';
        if (header.sortOrder === 'ASC') _sortOrder = 'DESC';
        if (header.sortOrder === 'DESC') _sortOrder = 'ASC';
        setHeader(headersUtil(headers, header, _sortOrder));
        dispatch(setOrder({by: header.property, direction: _sortOrder}));
        onSelectPage(
            1,
            {
                [header.property]: _sortOrder.toLowerCase()
            }
        );
    };

    const onSelectPage = (page) => {
        dispatch(setCurrentPage(page));
        dispatch(getManagedMatchTemplates());
    };

    const [headers, setHeader] = useState(createHeader().map(hc =>
        assign(hc, {
            sortOrder: null,
            handleSort: value => handleSort(value),
        })));

    return (
        <div>
            <TableWithPaging
                id={'source-table-with-paging'}
                headers={headers}
                rows={managedMatchTemplatesState.managedMatchTemplates}
                countOfResults={managedMatchTemplatesState.totalRecords}
                resultsPerPage={managedMatchTemplatesState.pageSize}
                currentPage={managedMatchTemplatesState.currentPage}
                onSelectPage={(page) => onSelectPage(page)}
            />
        </div>
    )
};


ManagedMatchTemplatesTable.propTypes = {
    managedMatchTemplates: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            description: PropTypes.string,
            type: PropTypes.string
        })
    ),
    handleSearch: PropTypes.func,
};

ManagedMatchTemplatesTable.defaultProps = {
    style: {},
    className: '',
    labelStyle: {},
    managedMatchTemplates: []
};

export default ManagedMatchTemplatesTable;
